import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { api } from '../lib/api';

// ──────────────────────────── Types ────────────────────────────
interface AusweisStats {
  users: number;
  ausweise: number;
  bedarfsausweise: number;
  thisMonth: number;
  thisWeek: number;
  byStatus: Array<{ status: string; count: number }>;
  byType: Array<{ type: string; count: number }>;
  recentAusweise: Array<Record<string, unknown>>;
}

interface Kunde {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  role: string;
  account_type: string;
  is_active: boolean;
  kundennummer: number;
  company_name: string | null;
  created_at: string;
  last_login_at: string | null;
  ausweis_count: number;
}

interface Ausweis {
  id: string;
  status: string;
  gebaeudetyp: string;
  baujahr: number;
  adresse_plz: string;
  adresse_ort: string;
  adresse_strasse: string;
  adresse_hausnummer: string;
  anzahl_wohneinheiten: number;
  wohnflaeche_m2: number;
  effizienzklasse: string;
  endenergie_kennwert: number;
  primaerenergie_kennwert: number;
  co2_emissionen: number;
  energietraeger: string;
  created_at: string;
  updated_at: string;
  pdf_generiert_am: string | null;
  kunde_email: string;
  kunde_first_name: string;
  kunde_last_name: string;
}

interface Pruefung {
  id: string;
  status: string;
  formular_daten: Record<string, unknown>;
  analyse_ergebnis: Record<string, unknown> | null;
  anmerkung: string | null;
  berater_notiz: string | null;
  bearbeitet_von: string | null;
  preis: number | null;
  created_at: string;
  updated_at: string;
  kunde_email: string;
  first_name: string;
  last_name: string;
}

interface Partner {
  id: string;
  company_name: string;
  contact_name: string;
  contact_email: string;
  phone: string;
  website: string;
  allowed_domains: string[];
  rate_limit_per_min: number;
  rate_limit_per_day: number;
  total_requests: number;
  total_ausweise: number;
  is_active: boolean;
  is_test_mode: boolean;
  plan: string;
  notes: string;
  created_at: string;
}

interface Rabattcode {
  id: string;
  code: string;
  bezeichnung: string;
  typ: string;
  wert: number;
  gueltig_ab: string;
  gueltig_bis: string | null;
  max_nutzungen: number | null;
  aktuelle_nutzungen: number;
  aktiv: boolean;
  hinweis: string;
  created_at: string;
}

interface Einstellung {
  schluessel: string;
  wert: string;
  beschreibung: string | null;
  updated_at: string;
}

// ──────────────────────────── Helpers ────────────────────────────
const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-700',
  pending: 'bg-yellow-100 text-yellow-700',
  review: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  completed: 'bg-emerald-100 text-emerald-700',
  offen: 'bg-orange-100 text-orange-700',
  in_bearbeitung: 'bg-blue-100 text-blue-700',
  freigegeben: 'bg-green-100 text-green-700',
  abgelehnt: 'bg-red-100 text-red-700',
  erledigt: 'bg-emerald-100 text-emerald-700',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf', pending: 'Ausstehend', review: 'In Prüfung',
  approved: 'Genehmigt', rejected: 'Abgelehnt', completed: 'Fertig',
  offen: 'Offen', in_bearbeitung: 'In Bearbeitung',
  freigegeben: 'Freigegeben', abgelehnt: 'Abgelehnt', erledigt: 'Erledigt',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status] ?? 'bg-slate-100 text-slate-600'}`}>
      {STATUS_LABELS[status] ?? status?.replace(/_/g, ' ') ?? '—'}
    </span>
  );
}

function StatCard({ label, value, sub, color = 'rla' }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
      <div className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">{label}</div>
      <div className={`text-3xl font-bold text-${color}-600`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

function formatDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function formatDateTime(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ──────────────────────────── Modal ────────────────────────────
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto m-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
        </div>
        <div className="px-6 py-4">{children}</div>
      </div>
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (<div className="mb-3"><label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>{children}</div>);
}

const inputCls = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rla-500/30';
const btnPrimary = 'px-4 py-2 bg-rla-600 text-white rounded-lg text-sm font-medium hover:bg-rla-700 disabled:opacity-50';
const btnSecondary = 'px-4 py-2 border border-slate-200 text-slate-600 rounded-lg text-sm hover:bg-slate-50';
const btnDanger = 'px-3 py-1.5 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100';
const btnEdit = 'px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100';

// ──────────────────────────── Tabs ────────────────────────────
type Tab = 'uebersicht' | 'kunden' | 'ausweise' | 'pruefungen' | 'partner' | 'rabatte' | 'einstellungen';
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'uebersicht', label: 'Übersicht' }, { id: 'kunden', label: 'Kunden' },
  { id: 'ausweise', label: 'Ausweise' }, { id: 'pruefungen', label: 'Prüfungen' },
  { id: 'partner', label: 'Partner' }, { id: 'rabatte', label: 'Rabattcodes' },
  { id: 'einstellungen', label: 'Einstellungen' },
];

// ──────────────────────────── Übersicht ────────────────────────────
function UebersichtTab() {
  const [stats, setStats] = useState<AusweisStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get<AusweisStats>('/ausweis/stats')
      .then(setStats).catch((e) => setError(e?.message || 'Fehler')).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-slate-500">Lade Statistiken…</div>;
  if (error || !stats) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <div className="text-red-600 font-medium mb-1">Fehler beim Laden</div>
      <div className="text-sm text-red-500 mb-3">{error || 'Keine Daten'}</div>
      <button onClick={() => window.location.reload()} className={btnPrimary}>Erneut versuchen</button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Registrierte Kunden" value={stats.users} color="blue" />
        <StatCard label="Verbrauchsausweise" value={stats.ausweise} sub={`+ ${stats.bedarfsausweise} Bedarfsausweise`} />
        <StatCard label="Diesen Monat" value={stats.thisMonth} sub={`${stats.thisWeek} diese Woche`} color="green" />
        <StatCard label="Gesamt" value={stats.ausweise + stats.bedarfsausweise} sub="Alle Ausweistypen" color="blue" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Status-Verteilung</h3>
          {stats.byStatus.length === 0 ? <div className="text-sm text-slate-400">Keine Daten</div> : (
            <div className="space-y-2">{stats.byStatus.map((s) => (
              <div key={s.status} className="flex items-center justify-between">
                <StatusBadge status={s.status} /><span className="text-sm font-mono text-slate-600">{s.count}</span>
              </div>
            ))}</div>
          )}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Gebäudetypen</h3>
          {stats.byType.length === 0 ? <div className="text-sm text-slate-400">Keine Daten</div> : (
            <div className="space-y-2">{stats.byType.map((t) => (
              <div key={t.type} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{t.type ?? 'Unbekannt'}</span><span className="font-mono text-slate-500">{t.count}</span>
              </div>
            ))}</div>
          )}
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Letzte Ausweise</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500 border-b">
              <th className="pb-2 pr-4">Datum</th><th className="pb-2 pr-4">Kunde</th>
              <th className="pb-2 pr-4">Ort</th><th className="pb-2 pr-4">Typ</th><th className="pb-2">Status</th>
            </tr></thead>
            <tbody>
              {stats.recentAusweise.map((a: Record<string, unknown>) => (
                <tr key={String(a.id)} className="border-b border-slate-50">
                  <td className="py-2 pr-4 text-slate-500">{formatDate(String(a.created_at))}</td>
                  <td className="py-2 pr-4">{String(a.first_name ?? '')} {String(a.last_name ?? '')} <span className="text-xs text-slate-400">({String(a.kunde_email ?? '—')})</span></td>
                  <td className="py-2 pr-4 text-slate-600">{String(a.adresse_plz ?? '')} {String(a.adresse_ort ?? '')}</td>
                  <td className="py-2 pr-4 text-slate-600">{String(a.gebaeudetyp ?? '—')}</td>
                  <td className="py-2"><StatusBadge status={String(a.status)} /></td>
                </tr>
              ))}
              {stats.recentAusweise.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-slate-400">Noch keine Ausweise</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────── Kunden ────────────────────────────
const emptyKunde = { email: '', first_name: '', last_name: '', phone: '', role: 'user', account_type: 'private' };

function KundenTab() {
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editKunde, setEditKunde] = useState<Partial<Kunde> | null>(null);
  const [saving, setSaving] = useState(false);
  const limit = 30;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(page * limit) });
      if (search) params.set('search', search);
      const data = await api.get<{ total: number; kunden: Kunde[] }>(`/ausweis/kunden?${params}`);
      setKunden(data.kunden); setTotal(data.total);
    } catch { /* */ }
    setLoading(false);
  }, [search, page]);

  useEffect(() => { void load(); }, [load]);

  const openCreate = () => { setEditKunde({ ...emptyKunde }); setShowModal(true); };
  const openEdit = (k: Kunde) => { setEditKunde({ ...k }); setShowModal(true); };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editKunde) return;
    setSaving(true);
    try {
      if (editKunde.id) { await api.put(`/ausweis/kunden/${editKunde.id}`, editKunde); }
      else { await api.post('/ausweis/kunden', editKunde); }
      setShowModal(false); setEditKunde(null); void load();
    } catch { /* */ }
    setSaving(false);
  };

  const handleDeactivate = async (id: string) => {
    if (!confirm('Kunde wirklich deaktivieren?')) return;
    try { await api.delete(`/ausweis/kunden/${id}`); void load(); } catch { /* */ }
  };

  const set = (key: string, val: unknown) => setEditKunde((prev) => prev ? { ...prev, [key]: val } : null);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <input type="text" placeholder="Suche nach Name oder E-Mail…" value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }} className={`flex-1 ${inputCls}`} />
        <span className="text-sm text-slate-400">{total} Kunden</span>
        <button onClick={openCreate} className={btnPrimary}>+ Neuer Kunde</button>
      </div>

      {loading ? <div className="text-slate-500">Lade…</div> : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500 bg-slate-50 border-b">
              <th className="px-4 py-3">Nr.</th><th className="px-4 py-3">Kunde</th><th className="px-4 py-3">E-Mail</th>
              <th className="px-4 py-3">Firma</th><th className="px-4 py-3">Typ</th>
              <th className="px-4 py-3 text-center">Ausweise</th><th className="px-4 py-3">Registriert</th>
              <th className="px-4 py-3">Aktionen</th>
            </tr></thead>
            <tbody>
              {kunden.map((k) => (
                <tr key={k.id} className={`border-b border-slate-50 hover:bg-slate-50/50 ${!k.is_active ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">{k.kundennummer}</td>
                  <td className="px-4 py-3 font-medium">{k.first_name} {k.last_name}</td>
                  <td className="px-4 py-3 text-slate-600">{k.email}</td>
                  <td className="px-4 py-3 text-slate-500">{k.company_name || '—'}</td>
                  <td className="px-4 py-3"><StatusBadge status={k.account_type} /></td>
                  <td className="px-4 py-3 text-center font-mono">{k.ausweis_count}</td>
                  <td className="px-4 py-3 text-slate-500">{formatDate(k.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(k)} className={btnEdit}>Bearbeiten</button>
                      {k.is_active && <button onClick={() => handleDeactivate(k.id)} className={btnDanger}>Deaktivieren</button>}
                    </div>
                  </td>
                </tr>
              ))}
              {kunden.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Keine Kunden gefunden</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {total > limit && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="px-3 py-1 text-sm border rounded disabled:opacity-40">Zurück</button>
          <span className="text-sm text-slate-500">Seite {page + 1} von {Math.ceil(total / limit)}</span>
          <button onClick={() => setPage(page + 1)} disabled={(page + 1) * limit >= total} className="px-3 py-1 text-sm border rounded disabled:opacity-40">Weiter</button>
        </div>
      )}

      {showModal && editKunde && (
        <Modal title={editKunde.id ? 'Kunde bearbeiten' : 'Neuer Kunde'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSave} className="space-y-1">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Vorname *"><input className={inputCls} required value={editKunde.first_name ?? ''} onChange={(e) => set('first_name', e.target.value)} /></FormField>
              <FormField label="Nachname *"><input className={inputCls} required value={editKunde.last_name ?? ''} onChange={(e) => set('last_name', e.target.value)} /></FormField>
            </div>
            <FormField label="E-Mail *"><input className={inputCls} type="email" required value={editKunde.email ?? ''} onChange={(e) => set('email', e.target.value)} /></FormField>
            <FormField label="Telefon"><input className={inputCls} value={editKunde.phone ?? ''} onChange={(e) => set('phone', e.target.value)} /></FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Rolle">
                <select className={inputCls} value={editKunde.role ?? 'user'} onChange={(e) => set('role', e.target.value)}>
                  <option value="user">User</option><option value="admin">Admin</option>
                </select>
              </FormField>
              <FormField label="Kontotyp">
                <select className={inputCls} value={editKunde.account_type ?? 'private'} onChange={(e) => set('account_type', e.target.value)}>
                  <option value="private">Privat</option><option value="business">Geschäftlich</option>
                </select>
              </FormField>
            </div>
            <div className="flex justify-end gap-2 pt-3">
              <button type="button" onClick={() => setShowModal(false)} className={btnSecondary}>Abbrechen</button>
              <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Speichern…' : 'Speichern'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ──────────────────────────── Ausweise ────────────────────────────
function AusweiseTab() {
  const [ausweise, setAusweise] = useState<Ausweis[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [editAusweis, setEditAusweis] = useState<Ausweis | null>(null);
  const [saving, setSaving] = useState(false);
  const limit = 30;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(page * limit) });
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const data = await api.get<{ total: number; ausweise: Ausweis[] }>(`/ausweis/ausweise?${params}`);
      setAusweise(data.ausweise); setTotal(data.total);
    } catch { /* */ }
    setLoading(false);
  }, [search, statusFilter, page]);

  useEffect(() => { void load(); }, [load]);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editAusweis) return;
    setSaving(true);
    try {
      await api.put(`/ausweis/ausweise/${editAusweis.id}`, {
        status: editAusweis.status, gebaeudetyp: editAusweis.gebaeudetyp, baujahr: editAusweis.baujahr,
        adresse_plz: editAusweis.adresse_plz, adresse_ort: editAusweis.adresse_ort,
        adresse_strasse: editAusweis.adresse_strasse, adresse_hausnummer: editAusweis.adresse_hausnummer,
        anzahl_wohneinheiten: editAusweis.anzahl_wohneinheiten, wohnflaeche_m2: editAusweis.wohnflaeche_m2,
        effizienzklasse: editAusweis.effizienzklasse, endenergie_kennwert: editAusweis.endenergie_kennwert,
        primaerenergie_kennwert: editAusweis.primaerenergie_kennwert, co2_emissionen: editAusweis.co2_emissionen,
        energietraeger: editAusweis.energietraeger,
      });
      setEditAusweis(null); void load();
    } catch { /* */ }
    setSaving(false);
  };

  const setA = (key: string, val: unknown) => setEditAusweis((prev) => prev ? { ...prev, [key]: val } : null);

  const effizienzColor = (klasse: string) => {
    const c: Record<string, string> = {
      'A+': 'text-green-700 bg-green-100', A: 'text-green-600 bg-green-50',
      B: 'text-lime-700 bg-lime-100', C: 'text-yellow-700 bg-yellow-100',
      D: 'text-orange-600 bg-orange-100', E: 'text-orange-700 bg-orange-100',
      F: 'text-red-600 bg-red-100', G: 'text-red-700 bg-red-100', H: 'text-red-800 bg-red-200',
    };
    return c[klasse] ?? 'text-slate-600 bg-slate-100';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <input type="text" placeholder="Suche nach Ort, PLZ oder E-Mail…" value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }} className={`flex-1 min-w-[200px] ${inputCls}`} />
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} className={inputCls + ' w-auto'}>
          <option value="">Alle Status</option>
          <option value="draft">Entwurf</option><option value="pending">Ausstehend</option>
          <option value="review">In Prüfung</option><option value="approved">Genehmigt</option>
          <option value="rejected">Abgelehnt</option><option value="completed">Fertig</option>
        </select>
        <span className="text-sm text-slate-400">{total} Ausweise</span>
      </div>

      {loading ? <div className="text-slate-500">Lade…</div> : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500 bg-slate-50 border-b">
                <th className="px-4 py-3">Datum</th><th className="px-4 py-3">Kunde</th>
                <th className="px-4 py-3">Adresse</th><th className="px-4 py-3">Typ</th>
                <th className="px-4 py-3 text-center">Klasse</th><th className="px-4 py-3 text-right">kWh/m²a</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3">Aktion</th>
              </tr></thead>
              <tbody>
                {ausweise.map((a) => (
                  <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-3 text-slate-500">{formatDate(a.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{a.kunde_first_name} {a.kunde_last_name}</div>
                      <div className="text-xs text-slate-400">{a.kunde_email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{a.adresse_strasse} {a.adresse_hausnummer}<br /><span className="text-xs">{a.adresse_plz} {a.adresse_ort}</span></td>
                    <td className="px-4 py-3 text-slate-600">{a.gebaeudetyp ?? '—'}</td>
                    <td className="px-4 py-3 text-center">
                      {a.effizienzklasse ? <span className={`px-2 py-0.5 rounded font-bold text-xs ${effizienzColor(a.effizienzklasse)}`}>{a.effizienzklasse}</span> : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-600">{a.endenergie_kennwert ? Number(a.endenergie_kennwert).toFixed(1) : '—'}</td>
                    <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                    <td className="px-4 py-3"><button onClick={() => setEditAusweis({ ...a })} className={btnEdit}>Bearbeiten</button></td>
                  </tr>
                ))}
                {ausweise.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Keine Ausweise gefunden</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {total > limit && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="px-3 py-1 text-sm border rounded disabled:opacity-40">Zurück</button>
          <span className="text-sm text-slate-500">Seite {page + 1} von {Math.ceil(total / limit)}</span>
          <button onClick={() => setPage(page + 1)} disabled={(page + 1) * limit >= total} className="px-3 py-1 text-sm border rounded disabled:opacity-40">Weiter</button>
        </div>
      )}

      {editAusweis && (
        <Modal title="Ausweis bearbeiten" onClose={() => setEditAusweis(null)}>
          <form onSubmit={handleSave} className="space-y-1">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Status">
                <select className={inputCls} value={editAusweis.status} onChange={(e) => setA('status', e.target.value)}>
                  <option value="draft">Entwurf</option><option value="pending">Ausstehend</option>
                  <option value="review">In Prüfung</option><option value="approved">Genehmigt</option>
                  <option value="rejected">Abgelehnt</option><option value="completed">Fertig</option>
                </select>
              </FormField>
              <FormField label="Gebäudetyp"><input className={inputCls} value={editAusweis.gebaeudetyp ?? ''} onChange={(e) => setA('gebaeudetyp', e.target.value)} /></FormField>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="col-span-2"><FormField label="Straße"><input className={inputCls} value={editAusweis.adresse_strasse ?? ''} onChange={(e) => setA('adresse_strasse', e.target.value)} /></FormField></div>
              <FormField label="Hausnr."><input className={inputCls} value={editAusweis.adresse_hausnummer ?? ''} onChange={(e) => setA('adresse_hausnummer', e.target.value)} /></FormField>
              <FormField label="PLZ"><input className={inputCls} value={editAusweis.adresse_plz ?? ''} onChange={(e) => setA('adresse_plz', e.target.value)} /></FormField>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <FormField label="Ort"><input className={inputCls} value={editAusweis.adresse_ort ?? ''} onChange={(e) => setA('adresse_ort', e.target.value)} /></FormField>
              <FormField label="Baujahr"><input className={inputCls} type="number" value={editAusweis.baujahr ?? ''} onChange={(e) => setA('baujahr', parseInt(e.target.value) || 0)} /></FormField>
              <FormField label="Wohneinheiten"><input className={inputCls} type="number" value={editAusweis.anzahl_wohneinheiten ?? ''} onChange={(e) => setA('anzahl_wohneinheiten', parseInt(e.target.value) || 0)} /></FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Wohnfläche m²"><input className={inputCls} type="number" step="0.1" value={editAusweis.wohnflaeche_m2 ?? ''} onChange={(e) => setA('wohnflaeche_m2', parseFloat(e.target.value) || 0)} /></FormField>
              <FormField label="Energieträger"><input className={inputCls} value={editAusweis.energietraeger ?? ''} onChange={(e) => setA('energietraeger', e.target.value)} /></FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Effizienzklasse">
                <select className={inputCls} value={editAusweis.effizienzklasse ?? ''} onChange={(e) => setA('effizienzklasse', e.target.value)}>
                  <option value="">—</option>
                  {['A+','A','B','C','D','E','F','G','H'].map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </FormField>
              <FormField label="Endenergie kWh/m²a"><input className={inputCls} type="number" step="0.01" value={editAusweis.endenergie_kennwert ?? ''} onChange={(e) => setA('endenergie_kennwert', parseFloat(e.target.value) || 0)} /></FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Primärenergie kWh/m²a"><input className={inputCls} type="number" step="0.01" value={editAusweis.primaerenergie_kennwert ?? ''} onChange={(e) => setA('primaerenergie_kennwert', parseFloat(e.target.value) || 0)} /></FormField>
              <FormField label="CO₂ kg/m²a"><input className={inputCls} type="number" step="0.01" value={editAusweis.co2_emissionen ?? ''} onChange={(e) => setA('co2_emissionen', parseFloat(e.target.value) || 0)} /></FormField>
            </div>
            <div className="flex justify-end gap-2 pt-3">
              <button type="button" onClick={() => setEditAusweis(null)} className={btnSecondary}>Abbrechen</button>
              <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Speichern…' : 'Speichern'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ──────────────────────────── Prüfungen ────────────────────────────
function PruefungenTab() {
  const [pruefungen, setPruefungen] = useState<Pruefung[]>([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (statusFilter) params.set('status', statusFilter);
      const data = await api.get<{ total: number; pruefungen: Pruefung[] }>(`/ausweis/pruefungen?${params}`);
      setPruefungen(data.pruefungen); setTotal(data.total);
    } catch { /* */ }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { void load(); }, [load]);

  const updateStatus = async (id: string, status: string) => {
    try { await api.put(`/ausweis/pruefungen/${id}/status`, { status }); void load(); } catch { /* */ }
  };

  // Adresse aus formular_daten extrahieren
  const getAddr = (fd: Record<string, unknown>) => {
    const s = fd?.adresse_strasse || fd?.strasse || '';
    const p = fd?.adresse_plz || fd?.plz || '';
    const o = fd?.adresse_ort || fd?.ort || '';
    return [s, p, o].filter(Boolean).join(', ');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={inputCls + ' w-auto'}>
          <option value="">Alle Status</option>
          <option value="offen">Offen</option><option value="in_bearbeitung">In Bearbeitung</option>
          <option value="freigegeben">Freigegeben</option><option value="abgelehnt">Abgelehnt</option>
          <option value="erledigt">Erledigt</option>
        </select>
        <span className="text-sm text-slate-400">{total} Prüfungsanfragen</span>
      </div>
      {loading ? <div className="text-slate-500">Lade…</div> : (
        <div className="space-y-3">
          {pruefungen.map((p) => (
            <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <StatusBadge status={p.status} />
                    <span className="text-xs text-slate-400">{formatDateTime(p.created_at)}</span>
                    {p.preis && <span className="text-xs font-mono text-slate-500">{Number(p.preis).toFixed(2)} €</span>}
                  </div>
                  <div className="text-sm font-medium">{p.first_name} {p.last_name} <span className="text-slate-400 font-normal">({p.kunde_email})</span></div>
                  {p.formular_daten && <div className="text-xs text-slate-500 mt-1">{getAddr(p.formular_daten)}</div>}
                  {p.anmerkung && <div className="text-sm text-slate-600 mt-1">{p.anmerkung}</div>}
                  {p.berater_notiz && <div className="text-sm text-slate-500 mt-2 pl-3 border-l-2 border-slate-200 italic">{p.berater_notiz}</div>}
                  {p.bearbeitet_von && <div className="text-xs text-slate-400 mt-1">Bearbeitet von: {p.bearbeitet_von}</div>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {p.status === 'offen' && <button onClick={() => updateStatus(p.id, 'in_bearbeitung')} className={btnEdit}>Bearbeiten</button>}
                  {(p.status === 'offen' || p.status === 'in_bearbeitung') && (
                    <>
                      <button onClick={() => updateStatus(p.id, 'freigegeben')} className="px-2 py-1 text-xs bg-green-50 text-green-600 rounded hover:bg-green-100">Freigeben</button>
                      <button onClick={() => updateStatus(p.id, 'abgelehnt')} className={btnDanger}>Ablehnen</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
          {pruefungen.length === 0 && <div className="text-center text-slate-400 py-8">Keine Prüfungsanfragen</div>}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────── Partner ────────────────────────────
const emptyPartner = { company_name: '', contact_name: '', contact_email: '', phone: '', website: '', rate_limit_per_min: 30, rate_limit_per_day: 5000, allowed_domains: '', plan: 'basic', notes: '' };

function PartnerTab() {
  const [partner, setPartner] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPartner, setEditPartner] = useState<Record<string, unknown> | null>(null);
  const [saving, setSaving] = useState(false);
  const [newApiKey, setNewApiKey] = useState<{ api_key: string; api_secret: string } | null>(null);

  const load = async () => {
    setLoading(true);
    try { const d = await api.get<{ partner: Partner[] }>('/ausweis/partner'); setPartner(d.partner); } catch { /* */ }
    setLoading(false);
  };
  useEffect(() => { void load(); }, []);

  const openCreate = () => { setEditPartner({ ...emptyPartner }); setShowModal(true); };
  const openEdit = (p: Partner) => {
    setEditPartner({ ...p, allowed_domains: Array.isArray(p.allowed_domains) ? p.allowed_domains.join(', ') : '' });
    setShowModal(true);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editPartner) return;
    setSaving(true);
    try {
      if (editPartner.id) {
        await api.put(`/ausweis/partner/${editPartner.id}`, editPartner);
        setShowModal(false); setEditPartner(null);
      } else {
        const res = await api.post<{ ok: boolean; api_key: string; api_secret: string }>('/ausweis/partner', editPartner);
        setShowModal(false); setEditPartner(null);
        if (res.api_key) setNewApiKey({ api_key: res.api_key, api_secret: res.api_secret });
      }
      void load();
    } catch { /* */ }
    setSaving(false);
  };

  const handleDeactivate = async (id: string) => {
    if (!confirm('Partner wirklich deaktivieren?')) return;
    try { await api.delete(`/ausweis/partner/${id}`); void load(); } catch { /* */ }
  };

  const set = (key: string, val: unknown) => setEditPartner((prev) => prev ? { ...prev, [key]: val } : null);

  if (loading) return <div className="text-slate-500">Lade…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-400">{partner.length} Partner</span>
        <button onClick={openCreate} className={btnPrimary}>+ Neuer Partner</button>
      </div>

      {newApiKey && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="text-sm font-semibold text-green-800 mb-2">Partner erstellt — API-Zugangsdaten (nur einmal sichtbar!)</div>
          <div className="font-mono text-xs bg-white p-3 rounded border">
            <div><strong>API Key:</strong> {newApiKey.api_key}</div>
            <div><strong>API Secret:</strong> {newApiKey.api_secret}</div>
          </div>
          <button onClick={() => setNewApiKey(null)} className="mt-2 text-xs text-green-600 hover:underline">Schließen</button>
        </div>
      )}

      {partner.length === 0 ? <div className="text-center text-slate-400 py-8">Keine Partner vorhanden</div> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {partner.map((p) => (
            <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-semibold text-slate-800">{p.company_name}</div>
                  <div className="text-xs text-slate-500">{p.contact_name} · {p.contact_email}</div>
                </div>
                <div className="flex gap-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {p.is_active ? 'Aktiv' : 'Inaktiv'}
                  </span>
                  {p.is_test_mode && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Test</span>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div><div className="text-lg font-bold text-slate-700">{p.total_ausweise}</div><div className="text-[10px] text-slate-400 uppercase">Ausweise</div></div>
                <div><div className="text-lg font-bold text-slate-700">{p.total_requests}</div><div className="text-[10px] text-slate-400 uppercase">Requests</div></div>
                <div><div className="text-lg font-bold text-slate-700">{p.plan}</div><div className="text-[10px] text-slate-400 uppercase">Plan</div></div>
              </div>
              {p.allowed_domains?.length > 0 && <div className="mt-3 text-xs text-slate-400">Domains: {p.allowed_domains.join(', ')}</div>}
              <div className="mt-2 text-xs text-slate-400">Limits: {p.rate_limit_per_min}/min · {p.rate_limit_per_day}/Tag · Seit {formatDate(p.created_at)}</div>
              <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                <button onClick={() => openEdit(p)} className={btnEdit}>Bearbeiten</button>
                {p.is_active && <button onClick={() => handleDeactivate(p.id)} className={btnDanger}>Deaktivieren</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && editPartner && (
        <Modal title={editPartner.id ? 'Partner bearbeiten' : 'Neuer Partner'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSave} className="space-y-1">
            <FormField label="Firmenname *"><input className={inputCls} required value={String(editPartner.company_name ?? '')} onChange={(e) => set('company_name', e.target.value)} /></FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Kontaktperson"><input className={inputCls} value={String(editPartner.contact_name ?? '')} onChange={(e) => set('contact_name', e.target.value)} /></FormField>
              <FormField label="Kontakt-E-Mail *"><input className={inputCls} type="email" required value={String(editPartner.contact_email ?? '')} onChange={(e) => set('contact_email', e.target.value)} /></FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Telefon"><input className={inputCls} value={String(editPartner.phone ?? '')} onChange={(e) => set('phone', e.target.value)} /></FormField>
              <FormField label="Website"><input className={inputCls} value={String(editPartner.website ?? '')} onChange={(e) => set('website', e.target.value)} /></FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Rate-Limit / Minute"><input className={inputCls} type="number" value={Number(editPartner.rate_limit_per_min ?? 30)} onChange={(e) => set('rate_limit_per_min', parseInt(e.target.value) || 0)} /></FormField>
              <FormField label="Rate-Limit / Tag"><input className={inputCls} type="number" value={Number(editPartner.rate_limit_per_day ?? 5000)} onChange={(e) => set('rate_limit_per_day', parseInt(e.target.value) || 0)} /></FormField>
            </div>
            <FormField label="Erlaubte Domains (kommasepariert)"><input className={inputCls} value={String(editPartner.allowed_domains ?? '')} onChange={(e) => set('allowed_domains', e.target.value)} /></FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Plan">
                <select className={inputCls} value={String(editPartner.plan ?? 'basic')} onChange={(e) => set('plan', e.target.value)}>
                  <option value="basic">Basic</option><option value="pro">Pro</option><option value="enterprise">Enterprise</option>
                </select>
              </FormField>
              <FormField label="Notizen"><input className={inputCls} value={String(editPartner.notes ?? '')} onChange={(e) => set('notes', e.target.value)} /></FormField>
            </div>
            <div className="flex justify-end gap-2 pt-3">
              <button type="button" onClick={() => setShowModal(false)} className={btnSecondary}>Abbrechen</button>
              <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Speichern…' : 'Speichern'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ──────────────────────────── Rabattcodes ────────────────────────────
const emptyRabatt = { code: '', bezeichnung: '', typ: 'prozent', wert: 0, gueltig_ab: '', gueltig_bis: '', max_nutzungen: 0, hinweis: '' };

function RabatteTab() {
  const [codes, setCodes] = useState<Rabattcode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editCode, setEditCode] = useState<Partial<Rabattcode> | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const d = await api.get<{ rabattcodes: Rabattcode[] }>('/ausweis/rabattcodes'); setCodes(d.rabattcodes); } catch { /* */ }
    setLoading(false);
  };
  useEffect(() => { void load(); }, []);

  const openCreate = () => { setEditCode({ ...emptyRabatt }); setShowModal(true); };
  const openEdit = (c: Rabattcode) => {
    setEditCode({
      ...c,
      gueltig_ab: c.gueltig_ab ? new Date(c.gueltig_ab).toISOString().slice(0, 10) : '',
      gueltig_bis: c.gueltig_bis ? new Date(c.gueltig_bis).toISOString().slice(0, 10) : '',
    });
    setShowModal(true);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editCode) return;
    setSaving(true);
    try {
      if (editCode.id) { await api.put(`/ausweis/rabattcodes/${editCode.id}`, editCode); }
      else { await api.post('/ausweis/rabattcodes', editCode); }
      setShowModal(false); setEditCode(null); void load();
    } catch { /* */ }
    setSaving(false);
  };

  const handleDeactivate = async (id: string) => {
    if (!confirm('Rabattcode deaktivieren?')) return;
    try { await api.delete(`/ausweis/rabattcodes/${id}`); void load(); } catch { /* */ }
  };

  const set = (key: string, val: unknown) => setEditCode((prev) => prev ? { ...prev, [key]: val } : null);

  if (loading) return <div className="text-slate-500">Lade…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-400">{codes.length} Rabattcodes</span>
        <button onClick={openCreate} className={btnPrimary}>+ Neuer Rabattcode</button>
      </div>

      {codes.length === 0 ? <div className="text-center text-slate-400 py-8">Keine Rabattcodes</div> : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500 bg-slate-50 border-b">
              <th className="px-4 py-3">Code</th><th className="px-4 py-3">Bezeichnung</th><th className="px-4 py-3">Rabatt</th>
              <th className="px-4 py-3">Gültig</th><th className="px-4 py-3 text-center">Nutzungen</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3">Aktionen</th>
            </tr></thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.id} className="border-b border-slate-50">
                  <td className="px-4 py-3 font-mono font-bold">{c.code}</td>
                  <td className="px-4 py-3 text-slate-600">{c.bezeichnung}</td>
                  <td className="px-4 py-3">{c.typ === 'prozent' ? `${c.wert}%` : `${c.wert}€`}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(c.gueltig_ab)} – {c.gueltig_bis ? formatDate(c.gueltig_bis) : '∞'}</td>
                  <td className="px-4 py-3 text-center font-mono">{c.aktuelle_nutzungen}/{c.max_nutzungen ?? '∞'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.aktiv ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {c.aktiv ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(c)} className={btnEdit}>Bearbeiten</button>
                      {c.aktiv && <button onClick={() => handleDeactivate(c.id)} className={btnDanger}>Deaktivieren</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && editCode && (
        <Modal title={editCode.id ? 'Rabattcode bearbeiten' : 'Neuer Rabattcode'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSave} className="space-y-1">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Code *"><input className={inputCls} required value={editCode.code ?? ''} onChange={(e) => set('code', e.target.value.toUpperCase())} placeholder="z.B. SOMMER25" /></FormField>
              <FormField label="Bezeichnung *"><input className={inputCls} required value={editCode.bezeichnung ?? ''} onChange={(e) => set('bezeichnung', e.target.value)} placeholder="z.B. Sommeraktion" /></FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Typ">
                <select className={inputCls} value={editCode.typ ?? 'prozent'} onChange={(e) => set('typ', e.target.value)}>
                  <option value="prozent">Prozent (%)</option><option value="betrag">Fester Betrag (€)</option>
                </select>
              </FormField>
              <FormField label="Wert *"><input className={inputCls} type="number" step="0.01" required value={editCode.wert ?? ''} onChange={(e) => set('wert', parseFloat(e.target.value) || 0)} /></FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Gültig ab"><input className={inputCls} type="date" value={editCode.gueltig_ab ?? ''} onChange={(e) => set('gueltig_ab', e.target.value)} /></FormField>
              <FormField label="Gültig bis"><input className={inputCls} type="date" value={editCode.gueltig_bis ?? ''} onChange={(e) => set('gueltig_bis', e.target.value)} /></FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Max. Nutzungen (leer = ∞)"><input className={inputCls} type="number" value={editCode.max_nutzungen ?? ''} onChange={(e) => set('max_nutzungen', e.target.value ? parseInt(e.target.value) : null)} /></FormField>
              <FormField label="Hinweis"><input className={inputCls} value={editCode.hinweis ?? ''} onChange={(e) => set('hinweis', e.target.value)} /></FormField>
            </div>
            <div className="flex justify-end gap-2 pt-3">
              <button type="button" onClick={() => setShowModal(false)} className={btnSecondary}>Abbrechen</button>
              <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Speichern…' : 'Speichern'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ──────────────────────────── Einstellungen ────────────────────────────
function EinstellungenTab() {
  const [einstellungen, setEinstellungen] = useState<Einstellung[]>([]);
  const [loading, setLoading] = useState(true);
  const [editKey, setEditKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const load = async () => {
    setLoading(true);
    try { const data = await api.get<{ einstellungen: Einstellung[] }>('/ausweis/einstellungen'); setEinstellungen(data.einstellungen); } catch { /* */ }
    setLoading(false);
  };
  useEffect(() => { void load(); }, []);

  const saveSetting = async (key: string, wert: string) => {
    try { await api.put(`/ausweis/einstellungen/${key}`, { wert }); setEditKey(null); void load(); } catch { /* */ }
  };

  const createSetting = async () => {
    if (!newKey.trim()) return;
    try { await api.post('/ausweis/einstellungen', { schluessel: newKey.trim(), wert: newValue, beschreibung: newDesc || undefined }); setShowNew(false); setNewKey(''); setNewValue(''); setNewDesc(''); void load(); } catch { /* */ }
  };

  const deleteSetting = async (key: string) => {
    if (!confirm(`"${key}" löschen?`)) return;
    try { await api.delete(`/ausweis/einstellungen/${key}`); void load(); } catch { /* */ }
  };

  if (loading) return <div className="text-slate-500">Lade…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-400">{einstellungen.length} Einstellungen</span>
        <button onClick={() => setShowNew(true)} className={btnPrimary}>+ Neue Einstellung</button>
      </div>

      {showNew && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Schlüssel</label><input className={inputCls} value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="z.B. preis_verbrauchsausweis" /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Wert</label><input className={inputCls} value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="z.B. 79.90" /></div>
          </div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Beschreibung</label><input className={inputCls} value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Optionale Beschreibung" /></div>
          <div className="flex gap-2"><button onClick={createSetting} className={btnPrimary}>Erstellen</button><button onClick={() => setShowNew(false)} className={btnSecondary}>Abbrechen</button></div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        {einstellungen.length === 0 ? <div className="p-6 text-center text-slate-400">Keine Einstellungen</div> : (
          <div className="divide-y divide-slate-100">
            {einstellungen.map((e) => (
              <div key={e.schluessel} className="px-5 py-4 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-700">{e.schluessel}</div>
                  {e.beschreibung && <div className="text-xs text-slate-400">{e.beschreibung}</div>}
                  <div className="text-xs text-slate-400">Zuletzt: {formatDateTime(e.updated_at)}</div>
                </div>
                {editKey === e.schluessel ? (
                  <div className="flex items-center gap-2">
                    <input className={inputCls + ' w-48'} value={editValue} onChange={(ev) => setEditValue(ev.target.value)}
                      onKeyDown={(ev) => { if (ev.key === 'Enter') saveSetting(e.schluessel, editValue); if (ev.key === 'Escape') setEditKey(null); }} autoFocus />
                    <button onClick={() => saveSetting(e.schluessel, editValue)} className="px-2 py-1 text-xs bg-green-50 text-green-600 rounded hover:bg-green-100">OK</button>
                    <button onClick={() => setEditKey(null)} className="px-2 py-1 text-xs bg-slate-50 text-slate-500 rounded hover:bg-slate-100">Abb.</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {e.wert === 'true' || e.wert === 'false' ? (
                      <button onClick={() => saveSetting(e.schluessel, e.wert === 'true' ? 'false' : 'true')}
                        className={`relative w-11 h-6 rounded-full transition-colors ${e.wert === 'true' ? 'bg-green-500' : 'bg-slate-300'}`}>
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${e.wert === 'true' ? 'translate-x-5' : ''}`} />
                      </button>
                    ) : (
                      <span className="text-sm font-mono text-slate-600 bg-slate-50 px-3 py-1 rounded max-w-[200px] truncate">{e.wert}</span>
                    )}
                    <button onClick={() => { setEditKey(e.schluessel); setEditValue(e.wert); }} className={btnEdit}>Bearbeiten</button>
                    <button onClick={() => deleteSetting(e.schluessel)} className={btnDanger}>Löschen</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────── Passwort-Sperre ────────────────────────────
function SperreControl() {
  const [aktiv, setAktiv] = useState(false);
  const [passwort, setPasswort] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get<{ aktiv: boolean; passwort: string }>('/ausweis/sperre')
      .then((data) => { setAktiv(data.aktiv); setPasswort(data.passwort || ''); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const handleToggle = async (newAktiv: boolean) => {
    if (newAktiv && !passwort.trim()) {
      setMsg('Bitte zuerst ein Passwort setzen.');
      return;
    }
    setSaving(true);
    setMsg('');
    try {
      await api.put('/ausweis/sperre', { aktiv: newAktiv, passwort: passwort.trim() || undefined });
      setAktiv(newAktiv);
      setMsg(newAktiv ? 'Seite gesperrt.' : 'Sperre aufgehoben.');
    } catch { setMsg('Fehler beim Speichern.'); }
    setSaving(false);
  };

  const handleSavePassword = async () => {
    if (!passwort.trim()) { setMsg('Passwort darf nicht leer sein.'); return; }
    setSaving(true);
    setMsg('');
    try {
      await api.put('/ausweis/sperre', { aktiv, passwort: passwort.trim() });
      setMsg('Passwort gespeichert.');
    } catch { setMsg('Fehler beim Speichern.'); }
    setSaving(false);
  };

  if (!loaded) return null;

  return (
    <div className={`rounded-xl border p-5 shadow-sm ${aktiv ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${aktiv ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-400'}`}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-800">Passwort-Sperre</div>
            <div className="text-xs text-slate-500">
              {aktiv ? 'Seite ist gesperrt — Besucher brauchen ein Passwort' : 'Seite ist frei zugänglich'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <input
              type={showPw ? 'text' : 'password'}
              value={passwort}
              onChange={(e) => setPasswort(e.target.value)}
              placeholder="Zugangspasswort"
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm w-44 focus:outline-none focus:ring-2 focus:ring-rla-500/30"
            />
            <button
              type="button"
              onClick={() => setShowPw(!showPw)}
              className="px-2 py-2 text-xs text-slate-400 hover:text-slate-600"
              title={showPw ? 'Verbergen' : 'Anzeigen'}
            >
              {showPw ? 'Verbergen' : 'Zeigen'}
            </button>
            <button onClick={handleSavePassword} disabled={saving}
              className="px-3 py-2 text-xs bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 disabled:opacity-50">
              Speichern
            </button>
          </div>
          <button
            onClick={() => handleToggle(!aktiv)}
            disabled={saving}
            className={`relative w-12 h-7 rounded-full transition-colors ${aktiv ? 'bg-amber-500' : 'bg-slate-300'} ${saving ? 'opacity-50' : ''}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full transition-transform shadow ${aktiv ? 'translate-x-5' : ''}`} />
          </button>
        </div>
      </div>
      {msg && (
        <div className={`mt-3 text-xs font-medium ${msg.includes('Fehler') || msg.includes('leer') ? 'text-red-600' : 'text-green-600'}`}>
          {msg}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────── Main Page ────────────────────────────
export function AusweisAdminPage() {
  const [activeTab, setActiveTab] = useState<Tab>('uebersicht');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Energieausweis-Portal</h1>
        <p className="text-sm text-slate-500">Verwaltung — rla-ausweise.de</p>
      </div>

      {/* Passwort-Sperre Steuerung */}
      <SperreControl />

      <div className="flex gap-1 overflow-x-auto border-b border-slate-200 pb-px">
        {TABS.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition ${
              activeTab === tab.id ? 'border-rla-500 text-rla-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}>{tab.label}</button>
        ))}
      </div>
      {activeTab === 'uebersicht' && <UebersichtTab />}
      {activeTab === 'kunden' && <KundenTab />}
      {activeTab === 'ausweise' && <AusweiseTab />}
      {activeTab === 'pruefungen' && <PruefungenTab />}
      {activeTab === 'partner' && <PartnerTab />}
      {activeTab === 'rabatte' && <RabatteTab />}
      {activeTab === 'einstellungen' && <EinstellungenTab />}
    </div>
  );
}
